<template>
  <div>
    <div class="display-1 font-weight-bold mt-1"><span>Sign in to AVA</span></div>
    <div class="mb-8 mt-1 grey--text title"><span>Enter your work email to continue</span></div>

    <v-form ref="internalForm" v-model="valid" lazy-validation @submit.prevent>
      <!-- Step 1: Email Input -->
      <v-label for="email-field" class="black--text font-weight-medium mb-2 d-block">Email Address</v-label>
      <v-text-field
        id="email-field"
        placeholder="you@company.com"
        outlined
        v-model="localForm.email"
        type="email"
        :rules="[formRules.validEmail]"
        :disabled="step === 2"
        @keyup.enter="onKeyUp"
        @input="emailError = null">
        <template v-slot:append v-if="step === 2">
          <v-btn icon small @click="resetToStep1" title="Change email">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
        </template>
      </v-text-field>

      <!-- Email Error Alert -->
      <v-alert
        v-if="emailError"
        type="error"
        dense
        text
        class="mb-4">
        {{ emailError }}
      </v-alert>

      <!-- Step 2: SSO Button or Password -->
      <template v-if="step === 2">
        <!-- SSO Login Option -->
        <div v-if="ssoInfo.ssoEnabled" class="mb-4">
          <SSOLoginButton
            :company-id="ssoInfo.companyId"
            :display-name="ssoInfo.ssoDisplayName"
            :loading="ssoLoading"
            @click="handleSSOLogin" />

          <!-- Divider if password login is also allowed -->
          <div v-if="ssoInfo.allowPasswordLogin" class="d-flex align-center my-4">
            <v-divider></v-divider>
            <span class="mx-4 grey--text text-body-2">or</span>
            <v-divider></v-divider>
          </div>
        </div>

        <!-- Password Login -->
        <template v-if="ssoInfo.allowPasswordLogin">
          <v-label for="password-field" class="black--text font-weight-medium mb-2 d-block">Password</v-label>
          <v-text-field
            id="password-field"
            placeholder="************"
            outlined
            v-model="localForm.password"
            :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append="togglePassword"
            :rules="[formRules.required]"
            hide-details="auto"
            :type="showPassword ? 'text' : 'password'"
            @keyup.enter="onKeyUp" />

          <div class="d-flex justify-space-between align-center">
            <v-checkbox
              color="primary"
              label="Remember me"
              v-model="mutableRememberMe"
              class="mr-4" />
            <router-link class="text-decoration-none black--text font-weight-medium" to="/forgotPassword">Forgot Password?</router-link>
          </div>

          <v-btn
            :loading="loading"
            large
            block
            :disabled="!valid || !localForm.password"
            color="primary"
            class="text-capitalize white--text login-btn"
            @click="submit">
            Sign In <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </template>

        <!-- SSO Only Mode (no password allowed) -->
        <v-alert
          v-if="ssoInfo.ssoEnabled && !ssoInfo.allowPasswordLogin"
          type="info"
          text
          class="mt-4">
          Your organization requires SSO login. Please use the "Login with Microsoft" button above.
        </v-alert>
      </template>

      <!-- Step 1: Continue Button -->
      <template v-if="step === 1">
        <v-btn
          :loading="checkingSSO"
          large
          block
          :disabled="!localForm.email || !isValidEmail"
          color="primary"
          class="text-capitalize white--text mt-4 login-btn"
          @click="checkEmailSSO">
          {{ checkingSSO ? 'Checking...' : 'Continue' }} <v-icon v-if="!checkingSSO" right>mdi-arrow-right</v-icon>
        </v-btn>
      </template>
    </v-form>
  </div>
</template>

<script>
import ssoService from '@/features/Company/service/sso-settings.service';
import SSOLoginButton from './SSOLoginButton.vue';
import { log, error } from '@/utils/debugLogger';

export default {
  name: 'LoginForm',
  components: {
    SSOLoginButton,
  },
  props: {
    loginForm: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    rememberMe: {
      type: Boolean,
      default: false,
    },
    formRules: {
      type: Object,
      required: true,
    },
    initialEmail: {
      type: String,
      default: '',
    },
    autoCheckEmail: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['submit', 'update:rememberMe', 'email-checked', 'step-changed'],
  data() {
    return {
      showPassword: false,
      valid: false,
      step: 1, // 1 = email entry, 2 = login options
      checkingSSO: false,
      ssoLoading: false,
      emailError: null,
      ssoInfo: {
        ssoEnabled: false,
        ssoDisplayName: 'Login with Microsoft',
        allowPasswordLogin: true,
        companyId: null,
      },
      localForm: this.loginForm,
      mutableRememberMe: this.rememberMe,
    };
  },
  computed: {
    isValidEmail() {
      if (!this.localForm.email) return false;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(this.localForm.email);
    },
  },
  watch: {
    mutableRememberMe(val) {
      this.$emit('update:rememberMe', val);
    },
    rememberMe(val) {
      this.mutableRememberMe = val;
    },
  },
  methods: {
    onKeyUp(ev) {
      if (ev.key !== 'Enter') return;
      if (this.step === 1) {
        this.checkEmailSSO();
      } else {
        this.submit();
      }
    },
    togglePassword() {
      this.showPassword = !this.showPassword;
    },
    async checkEmailSSO() {
      if (!this.isValidEmail) return;

      this.checkingSSO = true;
      this.emailError = null;
      try {
        const res = await ssoService.checkEmailSSO(this.localForm.email);
        const data = res.data;

        // Check if user doesn't exist and has no login options
        if (!data.ssoAvailable && !data.passwordLoginAllowed && !data.userExists) {
          this.emailError = 'Invalid email';
          return;
        }

        // Map API response to internal ssoInfo structure
        this.ssoInfo = {
          ssoEnabled: data.ssoAvailable || false,
          ssoDisplayName: data.ssoDisplayName || 'Login with Microsoft',
          allowPasswordLogin: data.passwordLoginAllowed !== false,
          companyId: data.companyId || null,
        };
        this.step = 2;
        log('SSO check result:', this.ssoInfo);

        // Emit event so parent can update URL with email
        this.$emit('email-checked', {
          email: this.localForm.email,
          ssoInfo: this.ssoInfo,
        });
        this.$emit('step-changed', 2);
      } catch (err) {
        error('Failed to check SSO:', err);
        // On error, proceed to step 2 with password-only login
        this.ssoInfo = {
          ssoEnabled: false,
          ssoDisplayName: 'Login with Microsoft',
          allowPasswordLogin: true,
          companyId: null,
        };
        this.step = 2;

        // Still emit so parent can update URL
        this.$emit('email-checked', {
          email: this.localForm.email,
          ssoInfo: this.ssoInfo,
        });
        this.$emit('step-changed', 2);
      } finally {
        this.checkingSSO = false;
      }
    },
    resetToStep1() {
      this.step = 1;
      this.localForm.password = '';
      this.ssoInfo = {
        ssoEnabled: false,
        ssoDisplayName: 'Login with Microsoft',
        allowPasswordLogin: true,
        companyId: null,
      };
      this.$emit('step-changed', 1);
    },
    handleSSOLogin() {
      this.ssoLoading = true;
      // The SSOLoginButton will handle the redirect
    },
    submit() {
      if (this.valid && this.localForm.password) {
        this.$emit('submit');
      }
    },
  },
  mounted() {
    // If initialEmail is provided, set it and optionally auto-check
    if (this.initialEmail) {
      this.localForm.email = this.initialEmail;
      if (this.autoCheckEmail && this.isValidEmail) {
        this.$nextTick(() => {
          this.checkEmailSSO();
        });
      }
    }
  },
};
</script>
